import React, { useState } from 'react';
import { X, User, Mail, Phone, Building, Utensils, MessageSquare, CreditCard } from 'lucide-react';

const RegistrationModal = ({ event, onClose, onRegister }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    dietary: '',
    specialRequests: ''
  });
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(1);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^\+?[\d\s-()]+$/.test(formData.phone)) {
      newErrors.phone = 'Phone number is invalid';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      if (step === 1) {
        setStep(2);
      } else {
        onRegister(event, formData);
        onClose();
      }
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return React.createElement('div', { 
    className: 'modal-overlay', 
    onClick: onClose 
  },
    React.createElement('div', { 
      className: 'modal-content', 
      onClick: (e) => e.stopPropagation() 
    },
      React.createElement('div', { className: 'modal-header' },
        React.createElement('h2', null, step === 1 ? 'Registration Details' : 'Payment Information'),
        React.createElement('button', { 
          onClick: onClose, 
          className: 'modal-close' 
        },
          React.createElement(X, { size: 24 })
        )
      ),
      
      React.createElement('div', { className: 'modal-body' },
        React.createElement('div', { className: 'event-summary' },
          React.createElement('img', { 
            src: event.image, 
            alt: event.title, 
            className: 'event-summary-image' 
          }),
          React.createElement('div', { className: 'event-summary-details' },
            React.createElement('h3', null, event.title),
            React.createElement('p', null, `${event.date} at ${event.time}`),
            React.createElement('p', { className: 'event-price' }, `$${event.price}`)
          )
        ),
        
        React.createElement('form', { 
          onSubmit: handleSubmit, 
          className: 'registration-form' 
        },
          step === 1 ? [
            React.createElement('div', { key: 'name', className: 'form-group' },
              React.createElement('div', { className: 'input-container' },
                React.createElement(User, { className: 'input-icon', size: 20 }),
                React.createElement('input', {
                  type: 'text',
                  placeholder: 'Full Name',
                  value: formData.name,
                  onChange: (e) => handleInputChange('name', e.target.value),
                  className: `form-input ${errors.name ? 'error' : ''}`
                })
              ),
              errors.name && React.createElement('span', { className: 'error-message' }, errors.name)
            ),
            
            React.createElement('div', { key: 'email', className: 'form-group' },
              React.createElement('div', { className: 'input-container' },
                React.createElement(Mail, { className: 'input-icon', size: 20 }),
                React.createElement('input', {
                  type: 'email',
                  placeholder: 'Email Address',
                  value: formData.email,
                  onChange: (e) => handleInputChange('email', e.target.value),
                  className: `form-input ${errors.email ? 'error' : ''}`
                })
              ),
              errors.email && React.createElement('span', { className: 'error-message' }, errors.email)
            ),
            
            React.createElement('div', { key: 'phone', className: 'form-group' },
              React.createElement('div', { className: 'input-container' },
                React.createElement(Phone, { className: 'input-icon', size: 20 }),
                React.createElement('input', {
                  type: 'tel',
                  placeholder: 'Phone Number',
                  value: formData.phone,
                  onChange: (e) => handleInputChange('phone', e.target.value),
                  className: `form-input ${errors.phone ? 'error' : ''}`
                })
              ),
              errors.phone && React.createElement('span', { className: 'error-message' }, errors.phone)
            ),
            
            React.createElement('div', { key: 'company', className: 'form-group' },
              React.createElement('div', { className: 'input-container' },
                React.createElement(Building, { className: 'input-icon', size: 20 }),
                React.createElement('input', {
                  type: 'text',
                  placeholder: 'Company (Optional)',
                  value: formData.company,
                  onChange: (e) => handleInputChange('company', e.target.value),
                  className: 'form-input'
                })
              )
            ),
            
            React.createElement('div', { key: 'dietary', className: 'form-group' },
              React.createElement('div', { className: 'input-container' },
                React.createElement(Utensils, { className: 'input-icon', size: 20 }),
                React.createElement('input', {
                  type: 'text',
                  placeholder: 'Dietary Requirements (Optional)',
                  value: formData.dietary,
                  onChange: (e) => handleInputChange('dietary', e.target.value),
                  className: 'form-input'
                })
              )
            ),
            
            React.createElement('div', { key: 'requests', className: 'form-group' },
              React.createElement('div', { className: 'input-container' },
                React.createElement(MessageSquare, { className: 'input-icon', size: 20 }),
                React.createElement('textarea', {
                  placeholder: 'Special Requests (Optional)',
                  value: formData.specialRequests,
                  onChange: (e) => handleInputChange('specialRequests', e.target.value),
                  className: 'form-textarea',
                  rows: 3
                })
              )
            )
          ] : 
          React.createElement('div', { className: 'payment-section' },
            React.createElement('div', { className: 'payment-summary' },
              React.createElement('h4', null, 'Payment Summary'),
              React.createElement('div', { className: 'payment-line' },
                React.createElement('span', null, 'Event Registration'),
                React.createElement('span', null, `$${event.price}`)
              ),
              React.createElement('div', { className: 'payment-line' },
                React.createElement('span', null, 'Processing Fee'),
                React.createElement('span', null, '$5.00')
              ),
              React.createElement('div', { className: 'payment-line total' },
                React.createElement('span', null, 'Total'),
                React.createElement('span', null, `$${event.price + 5}`)
              )
            ),
            
            React.createElement('div', { className: 'form-group' },
              React.createElement('div', { className: 'input-container' },
                React.createElement(CreditCard, { className: 'input-icon', size: 20 }),
                React.createElement('input', {
                  type: 'text',
                  placeholder: 'Card Number',
                  className: 'form-input'
                })
              )
            ),
            
            React.createElement('div', { className: 'form-row' },
              React.createElement('div', { className: 'form-group' },
                React.createElement('input', {
                  type: 'text',
                  placeholder: 'MM/YY',
                  className: 'form-input'
                })
              ),
              React.createElement('div', { className: 'form-group' },
                React.createElement('input', {
                  type: 'text',
                  placeholder: 'CVV',
                  className: 'form-input'
                })
              )
            ),
            
            React.createElement('div', { className: 'form-group' },
              React.createElement('input', {
                type: 'text',
                placeholder: 'Cardholder Name',
                className: 'form-input'
              })
            )
          ),
          
          React.createElement('div', { className: 'form-actions' },
            step === 2 &&
              React.createElement('button', {
                type: 'button',
                onClick: () => setStep(1),
                className: 'btn-secondary'
              }, 'Back'),
            React.createElement('button', { 
              type: 'submit', 
              className: 'btn-primary' 
            }, step === 1 ? 'Continue to Payment' : 'Complete Registration')
          )
        )
      )
    )
  );
};

export default RegistrationModal;